import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get, template } from 'lodash';
import RenderingTile from 'components/molecules/RenderingTile';
import { reduceCreativesData, reduceNameBasedData } from 'helpers/data';
import { formatPercentage } from 'helpers/formatNumbers';
import { getActivitiesSettings, getAudioObjectiveSettings } from 'helpers/objectiveSettings';
import { convertToDisplayValue } from 'helpers/strings';

// Charts
import BarChart from 'components/molecules/charts/BarChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';
import LineChart from 'components/molecules/charts/LineChart';

//  Tables
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';

function percentageToolTipFormatter(format) {
    return template(format, {
        interpolate: /{([\s\S]+?)}/g
    })({
        series: this.series,
        point: {
            ...this,
            y: formatPercentage(this.y)
        }
    });
}

function percentageFormatter() {
    return formatPercentage(this.value, { format: '0' });
}

class AudioExpandModule extends Component {
    render() {
        const stub = 'l3-site-audio';
        const { backfillBuckets, xAxisLabel, entityObjective, showTotalActivities } = this.props;
        const {
            eCPAActivitiesOrClicks, calloutTitle
        } = getActivitiesSettings(entityObjective);

        const hasPlacementsData = get(this.props, 'placements.params.hasData', null);
        const hasTargetingTacticsData = get(this.props, 'channel.targetingTactics.params.hasData', null);
        const hasChannelData = get(this.props, 'channel.hasData', false);
        const hasCreativesData = get(this.props, 'channel.creatives.params.hasData', null);

        const data = get(this.props, 'channel.data', {});
        const placements = get(this.props, 'placements', {});
        const targetingTactics = get(this.props, 'channel.targetingTactics', {});
        const creatives = get(this.props, 'channel.creatives', {});

        let placementsData = null;
        const placementsParams = get(this.props, 'placements.params', {});
        if (hasPlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }

        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'channel.targetingTactics.params', {});
        if (hasTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }

        let creativesData = null;
        const creativesParams = get(this.props, 'channel.creatives.params', {});
        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives);
        }

        // Data source can be ttd or dcm
        // Objective can be ecpa or ctr
        const dataSource = get(this.props, 'entityMeta.channelDataSources.audio', 'ttd');
        const {
            creativeColumns,
            placementsColumns,
            targetingTacticsColumns,
            placementsDefaultSorted,
            showCompletionRate
        } = getAudioObjectiveSettings(dataSource, entityObjective, eCPAActivitiesOrClicks, calloutTitle);

        return (
            <React.Fragment>
                {hasChannelData ? (
                    <BarChart
                        title="AUDIO IMPRESSIONS"
                        exportName={`${stub}-impressions`}
                        data={data}
                        backfillBuckets={backfillBuckets}
                        y="impressions"
                        yAxisLabel="Impressions"
                        color={this.props.theme.orderedColors[0]}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasChannelData && showCompletionRate ? (
                    <LineChart
                        title="COMPLETION RATE"
                        exportName={`${stub}-completion-rate`}
                        data={{ audio: data }}
                        backfillBuckets={backfillBuckets}
                        yTitle="Completion Rate"
                        yOptions={{
                            tooltip: {
                                pointFormatter: percentageToolTipFormatter
                            }
                        }}
                        yAxisOptions={{
                            labels: {
                                formatter: percentageFormatter
                            }
                        }}
                        y="completion_rate"
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData && showCompletionRate} />
                )}

                {hasPlacementsData ? (
                    <GroupedBarChart
                        title="PLACEMENT PERFORMANCE"
                        exportName={`${stub}-placement-performance`}
                        data={placementsData.graph}
                        keys={placementsData.sortedKeys}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={convertToDisplayValue(eCPAActivitiesOrClicks)}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasPlacementsData} />
                )}

                {hasPlacementsData ? (
                    <PlacementsTable
                        title="PLACEMENT ACTIVITY BREAKDOWN"
                        exportName={`${stub}-placement-activity-breakdown`}
                        data={placementsData.table}
                        params={placementsParams}
                        hasData={hasPlacementsData}
                        showTotalActivities={showTotalActivities}
                        defaultSorted={placementsDefaultSorted}
                        accessors={placementsColumns} />
                ) : (
                    <RenderingTile height='400px' show={hasPlacementsData} />
                )}

                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasTargetingTacticsData}
                    showTotalActivities={showTotalActivities}
                    accessors={targetingTacticsColumns}
                    // Same data format as Placements, so use those defaults
                    defaultSorted={placementsDefaultSorted}
                />

                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    showTotalActivities={showTotalActivities}
                    accessors={creativeColumns}
                />
            </React.Fragment>
        );
    }
}


AudioExpandModule.propTypes = {
    xAxisLabel: PropTypes.string,
    backfillBuckets: PropTypes.array,
    channel: PropTypes.object,
    placements: PropTypes.object,
    activities: PropTypes.object
};

export default AudioExpandModule;



// WEBPACK FOOTER //
// ./src/components/organisms/siteDirect/AudioExpandModule.js